import React, { useState, useRef, useEffect } from 'react';
import { Camera, Video, Settings2, Shield, Download, AlertCircle, Maximize, Play, Pause, Save, CheckCircle2, Loader2, Gauge, MousePointer2, Star, Trash2, RefreshCcw, History, Lock as LucideLock } from 'lucide-react';
import { SimulationConfig } from '@/types';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { InfoPopup } from './InfoPopup';
import { AUDIO } from '@/services/AudioService';
import { useAuth } from '@/context/auth-context';
import { usePayment } from '@/contexts/PaymentContext';
import { motion, AnimatePresence } from 'motion/react';
import { apiFetch } from '@/lib/api';

interface StudioRecorderTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  bgCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  webglCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  sim2dCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const StudioRecorderTab: React.FC<StudioRecorderTabProps> = ({
  config, setConfig, canvasRef, bgCanvasRef, webglCanvasRef, sim2dCanvasRef, isLocked, minPlan, onUpgradeClick
}) => {
  const { user } = useAuth();
  const { subscription } = usePayment();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedTime, setRecordedTime] = useState(0);
  const [exportCount, setExportCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [rngSeed, setRngSeed] = useState<string>(config.rngSeed || Math.random().toString(36).substring(7));
  const [renderDuration, setRenderDuration] = useState(30);
  const [activeJobs, setActiveJobs] = useState<any[]>([]);
  const [isRefreshingJobs, setIsRefreshingJobs] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const isCloudflarePages = typeof window !== 'undefined' && window.location.hostname.includes('pages.dev');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<any>(null);
  const activeJobsRef = useRef<any[]>([]);
  const exportWorkerRef = useRef<Worker | null>(null);
  const [isDraining, setIsDraining] = useState(false);

  useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  // Mime Type Detection
  const getSupportedMimeType = (format: 'mp4' | 'webm') => {
    const types = format === 'mp4' 
      ? [
          'video/mp4;codecs=h264,aac', 
          'video/mp4;codecs=h264', 
          'video/mp4',
          'video/webm;codecs=h264' // Some browsers use webm container with h264
        ]
      : [
          'video/webm;codecs=vp9,opus', 
          'video/webm;codecs=vp8,opus', 
          'video/webm'
        ];
        
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    
    // Final fallback: just try common ones
    const fallbacks = ['video/webm', 'video/mp4', 'video/ogg'];
    for (const type of fallbacks) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    
    return '';
  };

  const resolutions = [
    { id: '720p', label: '720p (HD)', target: 720, minPlan: 'FREE' },
    { id: '1080p', label: '1080p (Full HD)', target: 1080, minPlan: 'PRO' },
    { id: '2k', label: '1440p (2K)', target: 1440, minPlan: 'PRO' },
    { id: '4k', label: '4k (Ultra HD)', target: 2160, minPlan: 'ELITE' },
    { id: 'custom', label: 'Custom', target: 0, minPlan: 'PRO' },
  ];

  const aspectRatios = [
    { id: '16:9', label: '16:9 (Landscape)', w: 16, h: 9 },
    { id: '9:16', label: '9:16 (Vertical)', w: 9, h: 16 },
    { id: '1:1', label: '1:1 (Square)', w: 1, h: 1 },
    { id: '4:3', label: '4:3 (Classic)', w: 4, h: 3 },
  ];

  const updateDimensions = (resId: string, aspectId: string) => {
    const res = resolutions.find(r => r.id === resId) || resolutions[0];
    const aspect = aspectRatios.find(a => a.id === aspectId) || aspectRatios[0];
    
    if (res.id === 'custom') {
      setConfig(prev => ({ 
        ...prev, 
        recorderResolution: 'custom', 
        recorderAspect: aspectId as any 
      }));
      return;
    }

    let w = 1920;
    let h = 1080;

    if (aspectId === '16:9') {
      h = res.target;
      w = Math.round((h / 9) * 16);
    } else if (aspectId === '9:16') {
      w = res.target;
      h = Math.round((w / 9) * 16);
    } else if (aspectId === '1:1') {
      w = res.target;
      h = res.target;
    } else if (aspectId === '4:3') {
      h = res.target;
      w = Math.round((h / 3) * 4);
    }

    setConfig(prev => ({
      ...prev,
      recorderResolution: resId as any,
      recorderAspect: aspectId as any,
      recorderWidth: w,
      recorderHeight: h,
      // If choosing a social aspect ratio, sync outside collider to help framing
      outsideColliderEnabled: aspectId !== '16:9' ? true : prev.outsideColliderEnabled,
      outsideColliderWidth: w,
      outsideColliderHeight: h
    }));
  };

  const detectedPlan = React.useMemo(() => {
    const normalize = (p: string) => {
      const up = (p || '').toUpperCase();
      if (up.includes('ELITE')) return 'ELITE';
      if (up.includes('PRO')) return 'PRO';
      if (up.includes('ENTERPRISE')) return 'ENTERPRISE';
      return 'FREE';
    };

    const userPlan = normalize(user?.plan || 'FREE');
    if (!subscription || (subscription.status !== 'active' && subscription.status !== 'cancelling')) {
      return userPlan;
    }
    
    return normalize(subscription.plan_id);
  }, [user?.plan, subscription]);

  const isStarter = detectedPlan === 'FREE';
  const isEnterprise = detectedPlan === 'ENTERPRISE';
  const isPro = detectedPlan === 'PRO' || detectedPlan === 'ELITE' || detectedPlan === 'ENTERPRISE';

  // Anti-Recording State
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);

  useEffect(() => {
    const handleBlur = () => setIsWindowBlurred(true);
    const handleFocus = () => setIsWindowBlurred(false);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    // If current selected resolution is locked for this user, or if none is selected, set to 720p
    const currentResId = config.recorderResolution;
    const res = resolutions.find(r => r.id === currentResId);
    
    // Downgrade if:
    // 1. No resolution is selected
    // 2. Selected resolution is not in our list (invalid)
    // 3. User is Starter but selected resolution requires PRO/ELITE
    if (!currentResId || !res || (res.minPlan !== 'FREE' && isStarter)) {
      updateDimensions('720p', config.recorderAspect || '16:9');
    }
  }, [detectedPlan, config.recorderResolution, isStarter]);

  useEffect(() => {
    // Initial fetch
    apiFetch('/api/studio/video-export')
      .then(res => res.json())
      .then(data => setExportCount(data.count || 0))
      .catch(err => console.error('Failed to export count:', err));

    refreshJobs();

    // Phase 4 Optimization: Only poll frequently if we have active, non-terminal jobs
    const pollId = setInterval(() => {
      const hasActiveJobs = activeJobsRef.current.some(j => !['COMPLETED', 'FAILED'].includes(j.status));
      if (hasActiveJobs) {
        refreshJobs();
      } else {
        // Slow poll (once every 30s) if idle
        // Using a 1/5 chance every 6s is roughly 30s
        if (Math.random() > 0.8) refreshJobs(); 
      }
    }, 6000);

    return () => clearInterval(pollId);
  }, []); // Run once on mount

  const refreshJobs = async () => {
    setIsRefreshingJobs(true);
    try {
      setLastError(null);
      const res = await apiFetch('/api/studio/render-status');
      
      if (res.status === 401) {
        setLastError('Authentication required. Please log in.');
        setActiveJobs([]);
        return;
      }
      
      if (!res.ok) {
        setLastError(`Server error: ${res.status}`);
        return;
      }

      const data = await res.json();
      if (Array.isArray(data)) {
        setActiveJobs(data);
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setLastError('Connection failed. Are you on the correct domain?');
    } finally {
      setIsRefreshingJobs(false);
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      const res = await apiFetch(`/api/studio/render-status/${jobId}`, {
        method: 'DELETE'
      });
      
      if (res.status === 405) {
        alert('Method Not Allowed: This environment does not support DELETE requests. Please use your Cloud Run .app domain.');
        return;
      }
      
      if (res.ok) {
        setActiveJobs(prev => prev.filter(j => j.id !== jobId));
      } else {
        const errorData = await res.json().catch(() => ({}));
        alert(`Failed to delete render: ${errorData.error || res.statusText}`);
      }
    } catch (err) {
      console.error('Failed to delete job', err);
      alert('Network error while deleting job.');
    }
  };

  const startCloudRender = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const res = await apiFetch('/api/studio/video-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolution: `${config.recorderWidth || 1920}x${config.recorderHeight || 1080}`,
          format: config.recorderFormat,
          config: { ...config, rngSeed },
          duration: renderDuration,
          isCloudRender: true
        })
      });
      
      const data = await res.json();
      if (!res.ok) {
        setExportError(data.message || data.error || 'Cloud export failed');
        return;
      }

      refreshJobs();
    } catch (err) {
      setExportError('Network error starting cloud render');
    } finally {
      setIsExporting(false);
    }
  };

  const startRecording = async () => {
    if (isRecording) return;
    
    // Ensure we have a valid resolution preset selected for the user's plan
    const currentRes = resolutions.find(r => r.id === config.recorderResolution);
    const isValid = currentRes && !(isStarter && currentRes.minPlan !== 'FREE');

    if (!config.recorderResolution || !isValid) {
      updateDimensions('720p', config.recorderAspect || '16:9');
      // We return here to let the state update; the user can click again or we could ideally wait.
      // But since updateDimensions is synchronous with setConfig (mostly), 
      // it's safer to just proceed if we can calculate the dimensions right now.
    }

    setExportError(null);

    // Recalculate targets based on what we just decided (or what was already there)
    const finalResId = (!config.recorderResolution || !isValid) ? '720p' : config.recorderResolution;
    const finalRes = resolutions.find(r => r.id === finalResId) || resolutions[0];
    const finalAspectId = config.recorderAspect || '16:9';
    const finalAspect = aspectRatios.find(a => a.id === finalAspectId) || aspectRatios[0];

    let targetWidth = 1280;
    let targetHeight = 720;

    if (finalAspectId === '16:9') {
      targetHeight = finalRes.target;
      targetWidth = Math.round((targetHeight / 9) * 16);
    } else if (finalAspectId === '9:16') {
      targetWidth = finalRes.target;
      targetHeight = Math.round((targetWidth / 9) * 16);
    } else if (finalAspectId === '1:1') {
      targetWidth = finalRes.target;
      targetHeight = finalRes.target;
    } else if (finalAspectId === '4:3') {
      targetHeight = finalRes.target;
      targetWidth = Math.round((targetHeight / 3) * 4);
    }

    // Detect best mime type
    const mimeType = getSupportedMimeType(config.recorderFormat || 'webm');
    
    if (!mimeType) {
      setExportError('No supported video format found in this browser.');
      return;
    }

    // Composite canvas strategy
    const recordingCanvas = document.createElement('canvas');
    recordingCanvas.width = targetWidth;
    recordingCanvas.height = targetHeight;
    const ctx = recordingCanvas.getContext('2d');
    if (!ctx) return;

    // Get streams
    let videoStream: MediaStream;
    const recorderMethod = config.recorderMethod || 'COMPOSITE';

    if (recorderMethod === 'DIRECT' && webglCanvasRef.current) {
        // LEGACY/DIRECT MODE: Capture the main simulation canvas directly for maximum performance
        videoStream = webglCanvasRef.current.captureStream(config.recorderFPS || 60);
    } else {
        // COMPOSITE MODE: Build the frame by compositing layers (Required for custom watermarks/UI)
        videoStream = recordingCanvas.captureStream(config.recorderFPS || 60);
    }

    const audioStream = AUDIO.getStream();
    
    const combinedStream = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...(audioStream ? audioStream.getAudioTracks() : [])
    ]);

    // Handle Quality Modes
    let bitrate = config.recorderBitrate || 8000000;
    if (config.recorderQualityMode === 'LOSSLESS') {
      bitrate = 50000000; // 50Mbps "Lossless" for web
    } else if (config.recorderQualityMode === 'CBR') {
      // Browsers often ignore CBR hints, but we try to set a firm target
      bitrate = config.recorderBitrate || 8000000;
    }

    const options: any = {
      mimeType,
      videoBitsPerSecond: bitrate
    };

    try {
      const recorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        // Handle export tracking
        setIsExporting(true);
        try {
          const res = await apiFetch('/api/studio/video-export', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resolution: `${targetWidth}x${targetHeight}`,
              format: config.recorderFormat
            })
          });
          
          if (!res.ok) {
            const data = await res.json();
            setExportError(data.message || 'Export failed');
            return;
          }

          setExportCount(prev => prev + 1);

          // Download file
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `hypnotic-simulation-${Date.now()}.${config.recorderFormat === 'mp4' ? 'mp4' : 'webm'}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } catch (err) {
          setExportError('Export tracking failed');
        } finally {
          setIsExporting(false);
        }
      };

      // Instantiate the Off-thread Compositor Worker using Vite module worker syntax
      const exportWorker = new Worker(new URL('../export.worker.ts', import.meta.url), { type: 'module' });
      exportWorkerRef.current = exportWorker;

      PerformanceTelemetry.recorderActive = true;
      PerformanceTelemetry.recorderCompositingTime = 0;
      PerformanceTelemetry.recorderBacklog = 0;
      PerformanceTelemetry.recorderFramesCompleted = 0;

      const plan = subscription?.plan_id || user?.plan || 'FREE';

      // Send INIT payload with all necessary details to the background worker
      exportWorker.postMessage({
        type: 'INIT',
        payload: {
          width: targetWidth,
          height: targetHeight,
          config,
          userId: user?.id || 'ANONYMOUS',
          userEmail: user?.email || '',
          plan
        }
      });

      // Handle worker responses in a non-blocking asynchronous callback
      exportWorker.onmessage = (e) => {
        const { type, bitmap, timestamp, durationMs, backlogSize } = e.data;

        if (type === 'TELEMETRY') {
          PerformanceTelemetry.recorderBacklog = backlogSize;
        } else if (type === 'FRAME_COMPLETED' && bitmap) {
          PerformanceTelemetry.recorderCompositingTime = durationMs;
          PerformanceTelemetry.recorderFramesCompleted += 1;
          PerformanceTelemetry.recorderBacklog = Math.max(0, PerformanceTelemetry.recorderBacklog - 1);

          // Draw the completed composite frame directly to the main thread recording canvas
          if (ctx && mediaRecorderRef.current && (mediaRecorderRef.current.state === 'recording' || isDraining)) {
            ctx.clearRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(bitmap, 0, 0);
          }
          // Memory safety: strictly close the ImageBitmap to prevent GPU VRAM leaks!
          bitmap.close();
        }
      };

      recorder.start();
      setIsRecording(true);
      setRecordedTime(0);

      let lastFrameTime = 0;
      const fpsBudgetMs = 1000 / (config.recorderFPS || 60);

      // Animation loop for recording
      const renderToRecorder = async () => {
        // Stop posting if media recorder is not running and we are not draining
        if (!mediaRecorderRef.current || (mediaRecorderRef.current.state !== 'recording' && !isDraining)) return;

        const now = performance.now();
        const elapsed = now - lastFrameTime;

        if (elapsed >= fpsBudgetMs - 1) {
          lastFrameTime = now;

          // Backlog-throttled queue: limit pipeline capacity to protect main-thread rendering fluidness
          if (PerformanceTelemetry.recorderBacklog < 12) {
            try {
              const bgCanvas = bgCanvasRef.current;
              const webglCanvas = webglCanvasRef.current;
              const sim2DCanvas = sim2dCanvasRef.current;
              const uiCanvas = canvasRef.current;

              // Parallelized asynchronous ImageBitmap captures of all active render sub-canvases
              const [bgBmp, glBmp, simBmp, uiBmp] = await Promise.all([
                bgCanvas ? createImageBitmap(bgCanvas).catch(() => null) : null,
                webglCanvas ? createImageBitmap(webglCanvas).catch(() => null) : null,
                sim2DCanvas ? createImageBitmap(sim2DCanvas).catch(() => null) : null,
                (!config.recorderHideUI && uiCanvas) ? createImageBitmap(uiCanvas).catch(() => null) : null
              ]);

              const layers: any[] = [];
              const transferables: Transferable[] = [];

              if (bgBmp) {
                layers.push({ name: 'bg', bitmap: bgBmp });
                transferables.push(bgBmp);
              }
              if (glBmp) {
                layers.push({ name: 'webgl', bitmap: glBmp });
                transferables.push(glBmp);
              }
              if (simBmp) {
                layers.push({ name: 'sim2d', bitmap: simBmp });
                transferables.push(simBmp);
              }
              if (uiBmp) {
                layers.push({ name: 'hud', bitmap: uiBmp });
                transferables.push(uiBmp);
              }

              if (exportWorkerRef.current && layers.length > 0) {
                exportWorkerRef.current.postMessage({
                  type: 'QUEUE_FRAME',
                  payload: {
                    layers,
                    timestamp: Date.now(),
                    isWindowBlurred
                  }
                }, transferables);

                PerformanceTelemetry.recorderBacklog += 1;
              } else {
                // Garbage cleanup of unused layouts
                for (const bmp of [bgBmp, glBmp, simBmp, uiBmp]) {
                  if (bmp) bmp.close();
                }
              }
            } catch (err) {
              console.error('[RecorderTab] Failed capturing layer bitmaps:', err);
            }
          }
        }

        requestAnimationFrame(renderToRecorder);
      };

      // Start the loop
      renderToRecorder();

      timerRef.current = setInterval(() => {
        setRecordedTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Recording initialization failed', err);
      setExportError('Could not start recorder');
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;

    // Check if there is still a backlog inside the worker frame-compositor queue.
    // If yes, we initiate the "Draining / pipeline-flushing" phase to ensure no trailing frames are lost!
    if (PerformanceTelemetry.recorderBacklog > 0) {
      setIsDraining(true);
      const startDrainTime = Date.now();
      
      const checkDrain = setInterval(() => {
        const queueEmpty = PerformanceTelemetry.recorderBacklog === 0;
        const timeout = Date.now() - startDrainTime > 4000; // 4 seconds max graceful drain timeout

        if (queueEmpty || timeout) {
          clearInterval(checkDrain);
          setIsDraining(false);
          actualStopAndCleanup();
        }
      }, 50);
    } else {
      actualStopAndCleanup();
    }
  };

  const actualStopAndCleanup = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);

    // Securely terminate the export worker postmessage loops to free threads and memory
    if (exportWorkerRef.current) {
      exportWorkerRef.current.postMessage({ type: 'RESET' });
      exportWorkerRef.current.terminate();
      exportWorkerRef.current = null;
    }

    PerformanceTelemetry.recorderActive = false;
    PerformanceTelemetry.recorderBacklog = 0;
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (isLocked) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-10">
        <div className="w-20 h-20 rounded-full bg-zinc-950 flex items-center justify-center mb-6 shadow-2xl">
           <Shield className="w-10 h-10 text-neon-purple shadow-neon" />
        </div>
        <h3 className="text-xl font-black uppercase tracking-tighter mb-2">Recording Studio Locked</h3>
        <p className="text-sm opacity-50 font-bold mb-8 italic">The video recording suite requires an active subscription to access high-def exports and social presets.</p>
        <button 
          onClick={onUpgradeClick}
          className="px-8 py-4 bg-neon-purple text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg shadow-purple-500/20 active:scale-95 transition-all"
        >
          Upgrade to {minPlan || 'PRO'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 py-4">
      {/* Plan Status Card */}
      <div className={`p-6 rounded-[2rem] border relative overflow-hidden ${config.darkMode ? 'bg-white/5 border-white/10' : 'bg-zinc-50 border-zinc-200 shadow-sm'}`}>
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.darkMode ? 'bg-white/10' : 'bg-white shadow-sm border border-zinc-200'}`}>
              <Video className="w-6 h-6 text-neon-cyan" />
            </div>
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest">Video Studio</h3>
              <p className="text-[10px] font-bold opacity-40 uppercase tracking-tighter">
                {isEnterprise ? 'Enterprise Unlimited Studio' : isStarter ? `${5 - exportCount} Exports Left This Month` : 'Unlimited Professional Exports'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-1">
            <span className="text-[8px] font-black uppercase tracking-widest opacity-40">Render Engine</span>
            <div className="flex bg-black/10 rounded-lg p-1">
               <div className="px-3 py-1 text-[8px] font-black uppercase rounded-md bg-white text-black shadow-sm">
                Local
               </div>
               <div className="px-3 py-1 text-[8px] font-black uppercase rounded-md text-zinc-500 opacity-50 cursor-not-allowed flex items-center gap-1">
                Cloud <Shield className="w-2 h-2" />
               </div>
            </div>
          </div>
        </div>

        {isStarter && (
          <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden mb-4">
            <motion.div 
               initial={{ width: 0 }}
               animate={{ width: `${(exportCount / 5) * 100}%` }}
               className="h-full bg-neon-cyan shadow-[0_0_10px_#06b6d4]"
            />
          </div>
        )}

        {isStarter && (
          <p className="text-[9px] font-black uppercase tracking-widest opacity-60">
            Starter members are limited to {5} exports / month with watermarks.
          </p>
        )}
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {exportError && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center gap-3"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            <p className="text-[9px] font-black uppercase tracking-widest">{exportError}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 gap-8">
        {/* Future Development: Cloud Rendering */}
        <div className="opacity-40 grayscale pointer-events-none relative group">
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <div className="bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10 shadow-2xl transform -rotate-2">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Future Development</span>
                </div>
            </div>
            <ControlGroup label="Experimental: Cloud Render (Coming Soon)">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between p-4 rounded-2xl bg-black/5 border border-black/5">
                        <div className="flex flex-col">
                            <span className="text-[9px] font-black uppercase tracking-widest opacity-40">Cloud Render Status</span>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-zinc-400" />
                                <span className="text-xs font-black uppercase tracking-tighter">Disabled</span>
                            </div>
                        </div>
                    </div>
                </div>
            </ControlGroup>
        </div>

        {/* Main Controls - Local Only */}
        <ControlGroup label="Local Studio Recorder">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between p-4 rounded-2xl bg-black/5 border border-black/5">
                <div className="flex flex-col">
                    <span className="text-[9px] font-black uppercase tracking-widest opacity-40">Status</span>
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-zinc-400'}`} />
                        <span className="text-xs font-black uppercase tracking-tighter">{isRecording ? 'Recording...' : 'Ready'}</span>
                    </div>
                </div>
                <div className="text-xl font-black font-mono">
                    {formatTime(recordedTime)}
                </div>
            </div>

            <div className="flex gap-2">
                {!isRecording ? (
                    <button 
                        onClick={startRecording}
                        disabled={isExporting}
                        className={`flex-1 h-16 rounded-2xl flex items-center justify-center gap-3 font-black uppercase tracking-widest text-[10px] shadow-lg transition-all active:scale-95 border ${
                            config.darkMode 
                                ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 shadow-cyan-500/10' 
                                : 'bg-white border-cyan-600/20 text-cyan-600 shadow-cyan-600/5'
                        }`}
                    >
                        {isExporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
                        Start Simulation Capture
                    </button>
                ) : (
                    <button 
                        onClick={stopRecording}
                        disabled={isDraining}
                        className={`flex-1 h-16 text-white rounded-2xl flex items-center justify-center gap-3 font-black uppercase tracking-widest text-[11px] shadow-lg transition-all active:scale-95 ${
                            isDraining 
                                ? 'bg-amber-600 shadow-amber-600/20 cursor-wait' 
                                : 'bg-red-500 shadow-red-500/20 animate-pulse'
                        }`}
                    >
                        {isDraining ? <Loader2 className="w-5 h-5 animate-spin" /> : <Pause className="w-5 h-5" />}
                        {isDraining ? 'Flushing Video Pipeline...' : 'Finish & Export'}
                    </button>
                )}
            </div>

            <div className="p-4 rounded-2xl bg-zinc-50 border border-zinc-200">
                <p className="text-[9px] font-bold opacity-40 uppercase tracking-widest leading-relaxed">
                    Local recording captures your browser window in real-time. 
                    Ensure hardware acceleration is enabled for the best performance at high resolutions.
                </p>
            </div>
          </div>
        </ControlGroup>

        {/* Gallery - Always Local for now */}
        <ControlGroup label="Studio Gallery" settingKey="recorderGallery">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <SectionLabel darkMode={config.darkMode} icon="fa-history">Cloud Render Vault</SectionLabel>
                    <button 
                        onClick={refreshJobs} 
                        disabled={isRefreshingJobs}
                        className={`flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-cyan-500 hover:underline transition-opacity ${isRefreshingJobs ? 'opacity-50' : 'opacity-100'}`}
                    >
                        {isRefreshingJobs ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                        Sync Vault
                    </button>
                </div>
                
                <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar relative">
                    <div className="absolute inset-0 z-20 flex items-center justify-center backdrop-blur-[1.5px] bg-cyan-950/10 rounded-2xl border border-cyan-500/10">
                        <div className={`px-4 py-2 rounded-full flex items-center gap-2 border shadow-2xl ${config.darkMode ? 'bg-[#0a0a0a] text-white border-white/10' : 'bg-white text-zinc-950 border-zinc-200'}`}>
                           <LucideLock className="w-3.5 h-3.5 text-cyan-500" />
                           <span className="text-[10px] font-black uppercase tracking-[0.15em] leading-none">Internal Protocol Access</span>
                        </div>
                    </div>
                    {lastError ? (
                        <div className="py-8 flex flex-col items-center justify-center gap-3 text-red-500/60 text-center px-4">
                            <AlertCircle className="w-6 h-6" />
                            <span className="text-[10px] font-black uppercase tracking-tighter">{lastError}</span>
                            {lastError.includes('domain') && (
                                <p className="text-[9px] opacity-40">Try accessing the app via your .run.app URL</p>
                            )}
                        </div>
                    ) : activeJobs.length === 0 ? (
                        <div className="py-8 flex flex-col items-center justify-center gap-3 opacity-40">
                            <History className="w-6 h-6" />
                            <span className="text-[10px] font-black uppercase tracking-tighter">No renders found</span>
                        </div>
                    ) : activeJobs.map(job => (
                        <div 
                            key={job.id} 
                            className={`p-4 rounded-2xl border transition-all ${
                                config.darkMode 
                                    ? 'bg-white/5 border-white/5 hover:border-white/10' 
                                    : 'bg-zinc-50 border-zinc-200 hover:border-zinc-300'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-black tracking-tighter opacity-70 truncate">{job.resolution}</span>
                                        <span className="text-[8px] font-bold opacity-30">{new Date(job.created_at * 1000).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`w-1.5 h-1.5 rounded-full ${
                                            job.status === 'COMPLETED' ? 'bg-emerald-500' : 
                                            job.status === 'FAILED' ? 'bg-red-500' : 'bg-neon-cyan animate-pulse'
                                        }`} />
                                        <span className={`text-[10px] font-black uppercase tracking-widest ${
                                            job.status === 'COMPLETED' ? 'text-emerald-500' : 
                                            job.status === 'FAILED' ? 'text-red-500' : 'text-neon-cyan'
                                        }`}>
                                            {job.status === 'PENDING' ? 'CONNECTING...' : job.status} {(job.status === 'PROCESSING' || job.status === 'UPLOADING' || job.status === 'STARTING') && job.progress ? `(${Math.round(job.progress * 100)}%)` : ''}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {job.status === 'COMPLETED' && job.output_url && (
                                        <a 
                                            href={job.output_url} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            className="w-10 h-10 flex items-center justify-center bg-emerald-500 text-white rounded-xl hover:scale-105 transition-transform"
                                            title="Open/Download Video"
                                        >
                                            <Download className="w-5 h-5" />
                                        </a>
                                    )}
                                    <button 
                                        onClick={() => deleteJob(job.id)}
                                        className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all ${
                                            config.darkMode 
                                                ? 'bg-red-500/10 border-red-500/20 text-red-500 hover:bg-red-500/20' 
                                                : 'bg-red-50 border-red-100 text-red-600 hover:bg-red-100 shadow-sm'
                                        }`}
                                        title="Delete Render"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                            
                            {job.status === 'FAILED' && job.error_message && (
                                <div className="mt-3 p-2 rounded-lg bg-red-500/10 text-red-500 text-[8px] font-bold uppercase tracking-tight flex items-start gap-2">
                                    <AlertCircle className="w-3 h-3 shrink-0" />
                                    {job.error_message}
                                </div>
                            )}

                            {(job.status === 'PROCESSING' || job.status === 'UPLOADING') && (
                                <div className="mt-3 w-full h-1 bg-black/5 rounded-full overflow-hidden">
                                    <motion.div 
                                        className="h-full bg-neon-cyan"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(job.progress || 0) * 100}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <p className="text-[8px] font-bold opacity-30 uppercase tracking-widest text-center">
                    Renders are stored in your cloud vault for 30 days.
                </p>
            </div>
        </ControlGroup>

        {/* Output Quality */}
        <ControlGroup label="Format & Quality" settingKey="recorderQuality">
          <div className="flex flex-col gap-6">
            <ControlGroup label="Social MP4 Mode" settingKey="recorderFormat" tooltip="MP4 offers better compatibility for Instagram/TikTok but WEBM is natively faster in browsers.">
              <Toggle active={config.recorderFormat === 'mp4'} onClick={() => setConfig({ ...config, recorderFormat: config.recorderFormat === 'mp4' ? 'webm' : 'mp4' })} color="bg-cyan-600" />
            </ControlGroup>

            <div className="flex flex-col gap-2">
                <SectionLabel darkMode={config.darkMode}>Resolution Preset</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                    {resolutions.map(r => {
                        const isLocked = r.minPlan !== 'FREE' && isStarter;
                        const active = config.recorderResolution === r.id;
                        
                        return (
                            <button
                                key={r.id}
                                disabled={isLocked}
                                onClick={() => updateDimensions(r.id, config.recorderAspect || '16:9')}
                                className={`p-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                                    isLocked 
                                    ? 'bg-zinc-100 border-zinc-200 text-zinc-300 cursor-not-allowed' 
                                    : active 
                                        ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-cyan-500/5' 
                                        : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm')
                                }`}
                            >
                                {isLocked && <Shield className="w-3 h-3" />}
                                {r.label}
                                {r.minPlan !== 'FREE' && !isLocked && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                            </button>
                        );
                    })}
                </div>
                {isStarter && (
                    <p className="text-[8px] font-bold text-amber-600 uppercase tracking-widest mt-1 italic">
                        * HD & 4K resolutions require a PRO or ELITE subscription.
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel darkMode={config.darkMode}>Social Aspect Ratio</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                    {aspectRatios.map(a => {
                        const active = config.recorderAspect === a.id;
                        return (
                            <button
                                key={a.id}
                                onClick={() => updateDimensions(config.recorderResolution || '720p', a.id)}
                                className={`p-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all ${active ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-cyan-500/5' : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm')}`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {config.recorderResolution === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                    <SmartInput 
                        label="Width (px)" 
                        value={config.recorderWidth || 1920} 
                        onChange={(v) => setConfig({ ...config, recorderWidth: v })} 
                        min={100}
                        max={4000}
                        darkMode={config.darkMode}
                    />
                    <SmartInput 
                        label="Height (px)" 
                        value={config.recorderHeight || 1080} 
                        onChange={(v) => setConfig({ ...config, recorderHeight: v })} 
                        min={100}
                        max={4000}
                        darkMode={config.darkMode}
                    />
                </div>
            )}

            <SmartInput 
              label="Bitrate (Mbps)" 
              value={(config.recorderBitrate || 8000000) / 1000000} 
              onChange={(v) => setConfig({ ...config, recorderBitrate: v * 1000000 })} 
              min={0.1}
              max={50}
              step={0.1}
              darkMode={config.darkMode}
              tooltip="Higher bitrate = better quality but larger files. 8Mbps is recommended for 1080p."
            />

            <div className="flex flex-col gap-2">
                <SectionLabel darkMode={config.darkMode}>Quality Mode</SectionLabel>
                <div className="grid grid-cols-3 gap-2">
                    {['CBR', 'VBR', 'LOSSLESS'].map(mode => {
                        const active = (config.recorderQualityMode || 'VBR') === mode;
                        return (
                            <button
                                key={mode}
                                onClick={() => setConfig({ ...config, recorderQualityMode: mode as any })}
                                className={`p-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all ${active ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-cyan-500/5' : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm')}`}
                            >
                                {mode}
                            </button>
                        );
                    })}
                </div>
                <p className="text-[8px] font-bold opacity-40 uppercase tracking-widest mt-1 italic">
                    * VBR is recommended for best compression. LOSSLESS uses ultra-high bitrate.
                </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-4">
                <SectionLabel darkMode={config.darkMode}>Recorder FPS</SectionLabel>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[8px] font-black uppercase tracking-widest opacity-40">Sync Sim</span>
                  <Toggle active={config.simulationFPS === config.recorderFPS} onClick={() => setConfig({ ...config, simulationFPS: config.simulationFPS ? undefined : config.recorderFPS })} />
                  <InfoPopup settingKey="recorderFPS" darkMode={config.darkMode} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[24, 30, 48, 60, 90, 120, 144].map(fps => {
                  const active = config.recorderFPS === fps;
                  const isLocked = fps > 60 && isStarter;
                  return (
                    <button
                      key={fps}
                      disabled={isLocked}
                      onClick={() => setConfig({ ...config, recorderFPS: fps as any, simulationFPS: config.simulationFPS ? fps : undefined })}
                      className={`p-3 rounded-xl border text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1 ${isLocked ? 'opacity-30 grayscale cursor-not-allowed' : active ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20 shadow-cyan-500/5' : (config.darkMode ? 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10' : 'bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm')}`}
                    >
                      {fps}
                      {isLocked && <LucideLock className="w-2.5 h-2.5" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-[8px] font-bold opacity-40 uppercase tracking-widest mt-1 italic">
                * 60 FPS is recommended. High FPS (120+) requires powerful hardware.
              </p>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel darkMode={config.darkMode}>Simulation Performance Lock</SectionLabel>
                <div className="flex flex-col gap-4">
                    <SmartInput 
                        label="Override Simulation FPS" 
                        value={config.simulationFPS || 0} 
                        onChange={(v) => setConfig({ ...config, simulationFPS: v })} 
                        min={0} 
                        max={240} 
                        step={1}
                        darkMode={config.darkMode}
                        tooltip="Lock the simulation engine to a specific frame rate. Set to 0 for unlimited (tied to display). Use 60 or 30 to save CPU/GPU resources."
                    />
                    <div className="grid grid-cols-4 gap-2">
                        {[0, 30, 60, 120].map(f => (
                            <button
                                key={f}
                                onClick={() => setConfig({ ...config, simulationFPS: f })}
                                className={`p-2 rounded-lg border text-[8px] font-black uppercase ${config.simulationFPS === f ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 'bg-transparent border-zinc-500/10 text-zinc-500'}`}
                            >
                                {f === 0 ? 'AUTO' : `${f} FPS`}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel darkMode={config.darkMode}>Recording Engine</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                    {[
                        { id: 'DIRECT', label: 'Smooth (Legacy)', desc: 'Direct canvas stream. Best FPS.' },
                        { id: 'COMPOSITE', label: 'Studio (New)', desc: 'Multi-layer composite. High quality.' }
                    ].map(m => (
                        <button
                            key={m.id}
                            onClick={() => setConfig({ ...config, recorderMethod: m.id as any })}
                            className={`flex flex-col items-center gap-1 p-4 rounded-2xl border transition-all text-center ${config.recorderMethod === m.id || (!config.recorderMethod && m.id === 'COMPOSITE') ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' : 'bg-white/5 border-white/5 text-zinc-500 opacity-50 hover:opacity-100 hover:bg-white/10'}`}
                        >
                            <span className="text-[10px] font-black uppercase tracking-widest">{m.label}</span>
                            <span className="text-[7px] font-bold opacity-60 uppercase">{m.desc}</span>
                        </button>
                    ))}
                </div>
            </div>
          </div>
        </ControlGroup>

        {/* Region Capture */}
        <ControlGroup label="Capture Area">
            <div className="flex flex-col gap-6">
                <ControlGroup label="Region Mode" tooltip="When enabled, only the defined square will be captured.">
                  <Toggle active={config.recorderRegionEnabled || false} onClick={() => setConfig({ ...config, recorderRegionEnabled: !config.recorderRegionEnabled })} />
                </ControlGroup>

                {config.recorderRegionEnabled && (
                    <div className="grid grid-cols-2 gap-4">
                        <SmartInput label="X Offset" value={config.recorderRegionX || 0} onChange={(v) => setConfig({ ...config, recorderRegionX: v })} min={0} max={4000} darkMode={config.darkMode} />
                        <SmartInput label="Y Offset" value={config.recorderRegionY || 0} onChange={(v) => setConfig({ ...config, recorderRegionY: v })} min={0} max={4000} darkMode={config.darkMode} />
                        <SmartInput label="Width" value={config.recorderRegionWidth || 500} onChange={(v) => setConfig({ ...config, recorderRegionWidth: v })} min={10} max={4000} darkMode={config.darkMode} />
                        <SmartInput label="Height" value={config.recorderRegionHeight || 500} onChange={(v) => setConfig({ ...config, recorderRegionHeight: v })} min={10} max={4000} darkMode={config.darkMode} />
                    </div>
                )}
            </div>
        </ControlGroup>

        {/* UI Overlay */}
        <ControlGroup label="Visual Overlays">
            <div className="flex flex-col gap-6">
                <ControlGroup label="Show HUD in Recording" tooltip="Disable this if you want a clean cinematic shot with no counters.">
                  <Toggle active={!config.recorderHideUI} onClick={() => setConfig({ ...config, recorderHideUI: !config.recorderHideUI })} />
                </ControlGroup>
                
                <ControlGroup label="Show Branding" tooltip="Toggle the studio watermark (only available on Pro plans to hide).">
                  <Toggle active={config.recorderShowWatermark || false} onClick={() => !isStarter && setConfig({ ...config, recorderShowWatermark: !config.recorderShowWatermark })} disabled={isStarter} />
                </ControlGroup>

                {isStarter && (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex gap-3">
                        <Star className="w-4 h-4 text-amber-500 shrink-0" />
                        <p className="text-[8px] font-black uppercase tracking-widest text-amber-600">Starter users cannot disable watermarks. Upgrade to remove branding.</p>
                    </div>
                )}
            </div>
        </ControlGroup>
      </div>
    </div>
  );
};
